import { useMemo } from "react";
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

/**
 * Maps the global MUI theme (theme.customColors) to the token names used by
 * the document studio UI, so the studio matches the rest of Regodit.
 */
export const useStudioTheme = () => {
  const theme = useTheme();
  const tc = theme.customColors;

  return useMemo(() => {
    const info = tc.status.info;

    const palette = {
      canvas: tc.background.header,
      canvasAlt: tc.background.cardHeader,
      paper: tc.background.main,
      paperMuted: tc.background.header,
      surface: tc.background.infoBox,
      ink: tc.text.strong,
      inkSoft: tc.text.secondary,
      inkFaint: tc.text.disabled,
      border: tc.border.main,
      borderStrong: tc.border.subtle,
      brand: info.main,
      brandSoft: tc.icons.primary,
      brandDeep: tc.button.dark,
      brandTint: tc.background.info,
      brandGradient: `linear-gradient(135deg, ${info.main} 0%, ${tc.button.primary} 55%, ${tc.button.dark} 100%)`,
      infoChipBg: info.bg,
      infoChipText: info.text,
      infoHover: alpha(info.main, 0.06),
      buttonPrimary: tc.button.primary,
      buttonHover: tc.button.hover,
      buttonText: tc.button.text,
      hoverBg: tc.background.hover,
      success: tc.status.success.main,
      successTint: tc.status.success.bg,
      warning: tc.status.warning.main,
      danger: tc.status.error.main,
    };

    const shadow = {
      sm: tc.shadow.card,
      md: tc.shadow.cardHover,
      lg: tc.shadow.main,
      brand: tc.shadow.filterChip,
      panel: "0 -1px 0 rgba(0,0,0,0.04), 0 -8px 24px rgba(0,0,0,0.04)",
      document: "0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.08)",
    };

    return {
      palette,
      shadow,
      fontStack: theme.typography.fontFamily,
      serifStack: theme.typography.h1.fontFamily,
      theme,
      tc,
    };
  }, [theme, tc]);
};
// Quick edit actions offered when a conversation is empty.
export const EDIT_SUGGESTIONS = [
  { label: "Set company name", fieldId: "companyName" },
  { label: "Change document author", fieldId: "documentAuthor" },
  { label: "Update reviewer", fieldId: "reviewer" },
  "Tighten the language and remove redundancy",
];

export const COMPOSER_PLACEHOLDERS = [
  "Describe the compliance document you need\u2026",
  "e.g. Draft an Acceptable Use Policy for remote employees",
  "e.g. Build a Data Processing Agreement for a new sub-processor",
  "e.g. Create an Incident Response Plan for a fintech product",
];
